import {
  ECLIPSE_BUSINESS_PRODUCT_FAMILY_ID,
  ECLIPSE_CORE_PRODUCT_FAMILY_ID,
} from '@wix/package-picker-shared';

export { QUICK_PAY_LIGHTBOX_SPEC } from '@wix/package-picker-shared-eclipse';

export const DATA_HOOKS = {
  shell: 'quick-pay-lightbox-shell',
  cardLayout: 'quick-pay-lightbox-card-layout',
  chrome: 'quick-pay-lightbox-chrome',
  root: 'quick-pay-lightbox',
  close: 'quick-pay-lightbox-close',
  guarantee: 'quick-pay-lightbox-guarantee',
  title: 'quick-pay-lightbox-title',
  recommendedLabel: 'quick-pay-lightbox-recommended-label',
  planName: 'quick-pay-lightbox-plan-name',
  planDescription: 'quick-pay-lightbox-plan-description',
  cta: 'quick-pay-lightbox-cta',
  price: 'quick-pay-lightbox-price',
  priceStrikethrough: 'quick-pay-lightbox-price-strikethrough',
  featureList: 'quick-pay-lightbox-feature-list',
  featureRow: 'quick-pay-lightbox-feature-row',
  featuresNote: 'quick-pay-lightbox-features-note',
  comparePlans: 'quick-pay-lightbox-compare-plans',
  titleSubhead: 'quick-pay-lightbox-title-subhead',
  planCard: 'quick-pay-lightbox-plan-card',
  actions: 'quick-pay-lightbox-actions',
  welcomeGiftBanner: 'quick-pay-lightbox-welcome-gift-banner',
  disclaimer: 'quick-pay-lightbox-disclaimer',
  flowRoot: 'quick-pay-lightbox-flow',
  flowHeader: 'quick-pay-lightbox-flow-header',
  flowTitle: 'quick-pay-lightbox-flow-title',
  flowClose: 'quick-pay-lightbox-flow-close',
  flowFrame: 'quick-pay-lightbox-flow-frame',
  flowFrameLoader: 'quick-pay-lightbox-flow-frame-loader',
} as const;

export const SUCCESS_PAGE_PATH_MARKER = '/premium-success-pages/';

export const MINI_PP_KEY_PREFIX = 'premium-purchase-plan.mini_pp_lightbox';

export const MINI_PP_KEYS = {
  titleHeadline: `${MINI_PP_KEY_PREFIX}.title_headline`,
  titleSubhead: `${MINI_PP_KEY_PREFIX}.title_subhead`,
  titleNoSale: `${MINI_PP_KEY_PREFIX}.heading_no_sale`,
  recommendedPlanForSite: `${MINI_PP_KEY_PREFIX}.recommended_plan_for_site`,
  recommendedPlanGeneric: `${MINI_PP_KEY_PREFIX}.recommended_plan_generic`,
  ctaContinue: `${MINI_PP_KEY_PREFIX}.cta_continue`,
  moreFeatures: `${MINI_PP_KEY_PREFIX}.more_features_row`,
  comparePlans: `${MINI_PP_KEY_PREFIX}.compare_plans`,
} as const;

export type WelcomeGiftAudience = 'new' | 'returning';

export type WelcomeGiftStage = 'age_1' | 'age_2' | 'age_3';

export const WELCOME_GIFT_BANNER_KEYS: Record<
  WelcomeGiftAudience,
  Record<WelcomeGiftStage, string>
> = {
  new: {
    age_1: `${MINI_PP_KEY_PREFIX}.welcome_gift.age_1`,
    age_2: `${MINI_PP_KEY_PREFIX}.welcome_gift.age_2`,
    age_3: `${MINI_PP_KEY_PREFIX}.welcome_gift.age_3`,
  },
  returning: {
    age_1: `${MINI_PP_KEY_PREFIX}.welcome_back_gift.age_1`,
    age_2: `${MINI_PP_KEY_PREFIX}.welcome_back_gift.age_2`,
    age_3: `${MINI_PP_KEY_PREFIX}.welcome_back_gift.age_3`,
  },
};

const MINI_PP_DEFAULT_COPY: Record<string, string> = {
  [`${MINI_PP_KEY_PREFIX}.cta_continue`]: 'Continue',
  [`${MINI_PP_KEY_PREFIX}.title_headline`]: '{percent}% OFF SALE',
  [`${MINI_PP_KEY_PREFIX}.title_subhead`]: 'Select Premium Plans',
  [`${MINI_PP_KEY_PREFIX}.welcome_gift.age_1`]:
    'Your Welcome Gift \u2014 3 Days Only!',
  [`${MINI_PP_KEY_PREFIX}.welcome_gift.age_2`]:
    'Your Welcome Gift \u2014 48 Hours Left!',
  [`${MINI_PP_KEY_PREFIX}.welcome_gift.age_3`]:
    'Your Welcome Gift \u2014 Last Day!',
  [`${MINI_PP_KEY_PREFIX}.welcome_back_gift.age_1`]:
    'Your Welcome Back Gift \u2014 3 Days Only!',
  [`${MINI_PP_KEY_PREFIX}.welcome_back_gift.age_2`]:
    'Your Welcome Back Gift \u2014 48 Hours Left!',
  [`${MINI_PP_KEY_PREFIX}.welcome_back_gift.age_3`]:
    'Your Welcome Back Gift \u2014 Last Day!',
  [`${MINI_PP_KEY_PREFIX}.more_features_row`]:
    '60+ additional powerful features',
  [`${MINI_PP_KEY_PREFIX}.plan_description_short.core`]:
    'Build a strong online presence.',
  [`${MINI_PP_KEY_PREFIX}.plan_description_short.business`]:
    'Get Pro tools to grow your business.',
};

export const miniPpDefault = (key: string): string | undefined =>
  MINI_PP_DEFAULT_COPY[key];

export const QUICK_PAY_VISIBLE_FEATURES = 4;

export const QUICK_PAY_CLOSE_REPORT_TIMEOUT_MS = 1000;

export const QUICK_PAY_MODAL_EXIT_MS = 400;

export const RECOMMENDED_LABEL_MAX_LINES = 2;

export const RECOMMENDED_LABEL_LINE_HEIGHT = 16;

export const SUCCESS_DISMISS_PATH_PATTERN = /\/dashboard\/[^/]+\/home(\/|$|\?)/;

export const SUCCESS_IM_DONE_DATA_HOOKS = [
  'footerImDone',
  'platform-success-page-footer-im-done',
];

export const SUCCESS_CONNECT_DOMAIN_DATA_HOOKS = [
  'footerConnectADomain',
  'platform-success-page-footer-connect-a-domain',
];

export const SUCCESS_CONNECT_DOMAIN_ROUTE =
  'add-domain?referralAdditionalInfo=HAS_DOMAIN-SEARCH_ANOTHER';

export const SUCCESS_CONNECT_DOMAIN_PATH_PATTERN =
  /\/dashboard\/[^/]+\/add-domain\/?($|\?)/;

export const SUCCESS_MULTIPLE_OPTIONS_PATH_PATTERN = /^\/get-domain(\/|$|\?)/;

export interface QuickPayFeature {
  translationKey: string;

  highlighted?: boolean;
}

interface QuickPayPlanContent {
  descriptionKey: string;
  features: QuickPayFeature[];
}

const SHARED_FEATURES: QuickPayFeature[] = [
  {
    translationKey: 'premium-purchase-plan.cards.feature.free_domain',
    highlighted: true,
  },
  {
    translationKey:
      'premium-purchase-plan.header.fallback_feature.remove_branding',
  },
  {
    translationKey:
      'premium-purchase-plan.header.fallback_feature.customer_care',
  },
  { translationKey: 'premium-purchase-plan.cards.feature.accept_payments' },
];

export const QUICK_PAY_PLAN_CONTENT: Record<string, QuickPayPlanContent> = {
  [ECLIPSE_CORE_PRODUCT_FAMILY_ID]: {
    descriptionKey: `${MINI_PP_KEY_PREFIX}.plan_description_short.core`,
    features: [
      ...SHARED_FEATURES,
      {
        translationKey:
          'premium-purchase-plan.cards.feature.marketing_suite.core',
      },
      { translationKey: 'premium-purchase-plan.cards.feature.ecommerce.basic' },
      {
        translationKey:
          'premium-purchase-plan.cards.feature.storage_space.core',
      },
      {
        translationKey:
          'premium-purchase-plan.cards.feature.site_contributors.core',
      },
    ],
  },
  [ECLIPSE_BUSINESS_PRODUCT_FAMILY_ID]: {
    descriptionKey: `${MINI_PP_KEY_PREFIX}.plan_description_short.business`,
    features: [
      ...SHARED_FEATURES,

      {
        translationKey:
          'premium-purchase-plan.package_picker.feature.pro_marketing_suite',
      },
      {
        translationKey:
          'premium-purchase-plan.package_picker.feature.pro_ecommerce',
      },
      {
        translationKey:
          'premium-purchase-plan.cards.feature.storage_space.business',
      },
      {
        translationKey:
          'premium-purchase-plan.cards.feature.site_contributors.business',
      },
    ],
  },
};
